import { Button, FieldMessage, Heading, Label, Select, Surface, TextInput } from '@elladx/design-system';
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Address } from '../types';
import { US_STATES, CANADIAN_PROVINCES } from '../constants/regions';
import { GIFT_OPTIONS, TSHIRT_SIZES } from '../constants/giftOptions';
import ProductField from './ProductField';
interface AddressFormProps {
    title: string;
    address: Address;
    onChange: (address: Address) => void;
    /** View-only: inputs become readOnly, selects disabled, but stay focusable. */
    readOnly?: boolean;
    /** Fully disable every control (e.g. while a save is in flight). */
    disabled?: boolean;
    /** Override the slug used for field ids; needed when several forms render at
     *  once (e.g. one per table row). Defaults to a slug of `title`. */
    idPrefix?: string;
    /** Render just the fields, without the `.form-section` wrapper + `<h2>` —
     *  for embedding inside a host that supplies its own heading/container. */
    hideHeader?: boolean;
    progressiveOptional?: boolean;
    /** Shipping requires a recipient phone; saved address-book contacts may omit it. */
    phoneRequired?: boolean;
    showProduct?: boolean;
    productRequired?: boolean;
    errors?: Partial<Record<keyof Address, string>>;
    optionalFooter?: ReactNode;
}
function AddressForm({ title, address, onChange, readOnly = false, disabled = false, idPrefix, hideHeader = false, progressiveOptional = false, phoneRequired = false, showProduct = false, productRequired = false, errors = {}, optionalFooter, }: AddressFormProps) {
    const [optionalOpen, setOptionalOpen] = useState(false);
    const fieldPrefix = idPrefix ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const regions = address.country === 'CA' ? CANADIAN_PROVINCES : US_STATES;
    useEffect(() => {
        if (errors.tshirt_size || errors.email) setOptionalOpen(true);
    }, [errors.tshirt_size, errors.email]);
    const handleChange = (field: keyof Address, value: string | boolean) => {
        onChange({
            ...address,
            [field]: value,
            ...(field === 'gift' && value !== 'Tshirt' ? { tshirt_size: '' } : {}),
            ...(field === 'country' && value !== address.country ? { state: '' } : {}),
        });
    };
    const fieldError = (field: keyof Address) => errors[field] ? <FieldMessage id={`${fieldPrefix}-${field}-error`} error>{errors[field]}</FieldMessage> : null;
    const accessibility = (field: keyof Address) => ({ invalid: Boolean(errors[field]), 'aria-describedby': errors[field] ? `${fieldPrefix}-${field}-error` : undefined });
    const textField = (field: keyof Address, label: string, required = false, full = false, type = 'text', placeholder = '') => (<div className={`form-group ${full ? 'full-width' : ''}`}>
        <Label htmlFor={`${fieldPrefix}-${field}`}>{label}</Label>
        <TextInput id={`${fieldPrefix}-${field}`} type={type} value={String(address[field] ?? '')} onChange={event => handleChange(field, field === 'zip' ? event.target.value.toUpperCase() : event.target.value)} required={required} readOnly={readOnly} placeholder={placeholder} maxLength={field === 'zip' ? 10 : field === 'phone' ? 32 : field === 'product_name' ? 200 : undefined} autoComplete={field === 'phone' ? 'tel' : undefined} {...accessibility(field)}/>
        {fieldError(field)}
      </div>);
    const body = (<fieldset disabled={disabled} className="address-fields">
        {textField('name', 'Name *', true, false, 'text', 'Full name')}
        {!progressiveOptional && textField('company', 'Company', false, false, 'text', 'Company name (optional)')}
        {textField('street1', 'Street address *', true, true, 'text', 'Street address')}
        {!progressiveOptional && textField('street2', 'Apt / Suite / Unit', false, true, 'text', 'Apartment, suite, unit (optional)')}
        {textField('city', 'City *', true, false, 'text', 'City')}
        <div className="form-group">
          <Label htmlFor={`${fieldPrefix}-state`}>{address.country === 'CA' ? 'Province' : 'State'} *</Label>
          <Select id={`${fieldPrefix}-state`} value={address.state || ''} onChange={event => handleChange('state', event.target.value)} required disabled={readOnly} {...accessibility('state')}>
            {regions.map(region => <option key={region.value} value={region.value}>{region.label}</option>)}
          </Select>
          {fieldError('state')}
        </div>
        <div className="form-group">
          <Label htmlFor={`${fieldPrefix}-country`}>Country *</Label>
          <Select id={`${fieldPrefix}-country`} value={address.country || 'US'} onChange={event => handleChange('country', event.target.value)} required disabled={readOnly} {...accessibility('country')}>
            <option value="US">United States</option><option value="CA">Canada</option>
          </Select>
          {fieldError('country')}
        </div>
        {textField('zip', address.country === 'CA' ? 'Postal code *' : 'ZIP code *', true, false, 'text', 'ZIP / postal code')}
        {phoneRequired && textField('phone', 'Phone *', true, false, 'tel', 'Phone number')}
        {showProduct && <ProductField idPrefix={fieldPrefix} value={address.product_name} onChange={value => handleChange('product_name', value)} required={productRequired} readOnly={readOnly} error={errors.product_name}/>}
        {progressiveOptional && <Button type="button" className="full-width" variant="ghost" aria-expanded={optionalOpen} aria-controls={`${fieldPrefix}-optional`} onClick={() => setOptionalOpen(value => !value)} disabled={readOnly}>{optionalOpen ? 'Hide optional details' : 'Add optional details'}</Button>}
        {(!progressiveOptional || optionalOpen) && <div id={`${fieldPrefix}-optional`} className="address-fields full-width">
        {progressiveOptional && textField('company', 'Company', false, false, 'text', 'Company name (optional)')}
        {progressiveOptional && textField('street2', 'Apartment, suite or unit', false, false, 'text', 'Apartment, suite, unit (optional)')}
        {!phoneRequired && textField('phone', 'Phone', false, false, 'tel', 'Phone number')}
        {textField('email', progressiveOptional ? 'Email for tracking' : 'Email', false, false, 'email', 'Email address (for tracking notifications)')}
        <div className={`form-group ${address.gift === 'Tshirt' ? '' : 'full-width'}`}>
          <Label htmlFor={`${fieldPrefix}-gift`}>Gift</Label>
          <Select id={`${fieldPrefix}-gift`} value={address.gift || ''} onChange={event => handleChange('gift', event.target.value)} disabled={readOnly}>
            {GIFT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </div>
        {address.gift === 'Tshirt' && <div className="form-group">
          <Label htmlFor={`${fieldPrefix}-tshirt-size`}>T-shirt size *</Label>
          <Select id={`${fieldPrefix}-tshirt-size`} value={address.tshirt_size || ''} onChange={event => handleChange('tshirt_size', event.target.value)} disabled={readOnly} required {...accessibility('tshirt_size')}>
            {TSHIRT_SIZES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
          {fieldError('tshirt_size')}
        </div>}
        {optionalFooter}
        </div>}
      </fieldset>);
    if (hideHeader)
        return body;
    return (<Surface className="form-section" aria-labelledby={`${fieldPrefix}-heading`} as="section">
      <Heading id={`${fieldPrefix}-heading`} as="h2" variant="card">{title}</Heading>
      {body}
    </Surface>);
}
export default AddressForm;
